import { useTranslation } from "next-i18next";
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { IProduct } from "@/props/IProduct";
import { useRouter } from "next/router";
import ProductShareModal from "../Modal/ProductShareModal";
import { Spin, message } from "antd";
import { HeartFilled, HeartOutlined, ShareAltOutlined } from "@ant-design/icons";
import { cartCountApi, cartSaveApi } from "@/api/cart";
import { addFavoriteApi } from "@/api/favorites";
import AddCartSuccessModal from "@/components/Modal/SuccessTipModal";
import debounce from "lodash/debounce";
import { useAppDispatch } from "@/store/hooks";
import { setCartTotal } from "@/store/cart";
import { getCookie } from "cookies-next";
import Icon from "../Icon";

// 骨架屏
const ProductItemSkeleton = () => {
  return (
    <div className="flex animate-pulse flex-col overflow-hidden">
      <div className="h-[43.73vw] w-full rounded-tl-xl rounded-tr-xl bg-gray-200 md:h-[30.12vw] lg:h-[240px] xl:h-[308px]" />
      <div className="flex flex-col rounded-bl-xl rounded-br-xl border border-gray-900 px-mobile py-2 md:px-4 md:py-6">
        <div className="mb-2 h-4 w-3/4 rounded bg-gray-300" />
        <div className="h-3 w-1/2 rounded bg-gray-300" />
      </div>
    </div>
  );
};

const ProductItemListSkeleton = () => {
  return (
    <div className="flex animate-pulse overflow-hidden rounded-xl border border-gray-900">
      <div className="h-[140px] w-[140px] shrink-0 bg-gray-200 xl:h-[180px] xl:w-[180px]" />
      <div className="flex flex-1 flex-col justify-center px-4 py-3">
        <div className="mb-3 h-4 w-3/4 rounded bg-gray-300" />
        <div className="mb-2 h-3 w-1/2 rounded bg-gray-300" />
        <div className="h-3 w-1/3 rounded bg-gray-300" />
      </div>
    </div>
  );
};

const ProductItem = ({
  product,
  loading = false,
  layout = "grid",
  showFavoriteIcon = false,
}: {
  product?: IProduct;
  loading?: boolean;
  layout?: "grid" | "list";
  showFavoriteIcon?: boolean;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const AddCartSuccessRef = useRef<any>(null);

  // 类型保护：product 可能 undefined，转成 null 或 IProduct
  const safeProduct = useMemo(() => product ?? null, [product]);

  const currentLocale = useMemo(() => (router.locale || "en").toUpperCase(), [router.locale]);

  const getCartNum = useCallback(async () => {
    if (!getCookie("token")) return;
    try {
      const data: any = await cartCountApi();
      const count = data["count"] || 0;
      dispatch(setCartTotal(count));
    } catch {
      // 错误处理可加
    }
  }, [dispatch]);

  const productId = useMemo(() => safeProduct?.productId ?? safeProduct?.id, [safeProduct]);

  const handleAddToCart = useCallback(async () => {
    if (!safeProduct || !safeProduct.stock) return;
    if (safeProduct.productAttrValueId !== undefined && safeProduct.productAttrValueId !== null) {
      const data = {
        productId: productId!,
        productAttrUnique: safeProduct.productAttrValueId,
        cartNum: 1,
      };
      setCartLoading(true);
      try {
        const isCartSave = await cartSaveApi(data);
        setCartLoading(false);
        if (isCartSave === undefined) return;
        AddCartSuccessRef.current.setOpen(true);
        await getCartNum();
      } catch (errorInfo) {
        console.log(t("common.error_occurred"));
        setCartLoading(false);
      }
    } else {
      router.push(
        {
          pathname: `/mall/[id]`,
          query: { id: productId },
        },
        undefined,
        { locale: router.locale }
      );
    }
  }, [safeProduct, router, getCartNum, t]);

  const debouncedHandleAddToCart = useMemo(() => debounce(handleAddToCart, 200), [handleAddToCart]);

  // 收藏（未登录跳登录页）
  const handleAddFavorite = useCallback(async () => {
    if (!safeProduct) return;
    if (!getCookie("token")) {
      router.push(`/login?redirect=${router.asPath}`);
      return;
    }
    setFavoriteLoading(true);
    try {
      await addFavoriteApi({ productId: productId! });
      message.success(t("favorites.favorite_success"));
    } catch (err) {
      console.log("favorite failed:", err);
    } finally {
      setFavoriteLoading(false);
    }
  }, [safeProduct, router, t]);

  const debouncedAddFavorite = useMemo(() => debounce(handleAddFavorite, 300), [handleAddFavorite]);

  useEffect(() => {
    return () => {
      debouncedHandleAddToCart.cancel();
      debouncedAddFavorite.cancel();
    };
  }, [debouncedHandleAddToCart, debouncedAddFavorite]);

  // 渲染骨架屏
  if (loading || !safeProduct) {
    return layout === "list" ? <ProductItemListSkeleton /> : <ProductItemSkeleton />;
  }

  // 以下安全使用 safeProduct
  const isOffShelf = !!safeProduct.isOffShelf;

  const SoldOutTip = () => (
    <div
      className="absolute z-10 flex h-[27vw] w-[27vw] items-center justify-center rounded-[50%] bg-[rgba(0,0,0,0.6)] text-base text-white group-hover:bg-[rgba(0,0,0,0.6)] 2xs:text-[20px] sm:text-2xl md:h-[18vw] md:w-[18vw] lg:h-36 lg:w-36 lg:group-hover:bg-transparent xl:h-40 xl:w-40 transition-colors duration-200"
      aria-label={t("product_detail.sell_out")}
      role="alert"
    >
      {t("product_detail.sell_out")}
    </div>
  );

  const OffShelfTip = () => (
    <div
      className="absolute z-10 flex h-[27vw] w-[27vw] items-center justify-center rounded-[50%] bg-[rgba(0,0,0,0.6)] text-base text-white 2xs:text-[20px] sm:text-2xl md:h-[18vw] md:w-[18vw] lg:h-36 lg:w-36 xl:h-40 xl:w-40"
      aria-label={t("favorites.off_shelves")}
      role="alert"
    >
      {t("favorites.off_shelves")}
    </div>
  );

  const DetailLink = ({ id, isMobile }: { id: number | string; isMobile?: boolean }) => {
    if (isOffShelf) return null;
    return (
      <Link
        href={`/mall/${id}`}
        locale={router.locale}
        className={`absolute left-0 top-0 z-20 h-full w-full ${isMobile ? "lg:hidden" : ""}`}
        aria-label={t("common.view_product_detail")}
        data-qt="mall_product_detail_click"
        data-qt-entry={isMobile ? "grid_image_mobile" : "grid_image_pc"}
      />
    );
  };

  // PC 收藏爱心 icon（hover 时显示）
  const FavoriteIcon = () => {
    if (!showFavoriteIcon) return null;
    return (
      <div
        className="absolute right-2 top-2 z-30 hidden h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 lg:flex"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          debouncedAddFavorite();
        }}
        data-qt="mall_product_favorite_click"
        data-qt-entry="grid_pc"
      >
        {favoriteLoading ? <Spin size="small" /> : <HeartFilled className="text-xl" style={{ color: "#d1d5db" }} />}
      </div>
    );
  };

  // Mobile 右侧悬浮操作栏（收藏 + 分享，白底圆形，上下排列）
  const MobileActionIcons = () => {
    if (!showFavoriteIcon) return null;
    return (
      <div className="absolute right-1.5 top-1.5 z-30 flex flex-col gap-1.5 lg:hidden">
        {/* 收藏 */}
        <div
          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-white shadow-sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            debouncedAddFavorite();
          }}
          data-qt="mall_product_favorite_click"
          data-qt-entry="grid_mobile"
        >
          {favoriteLoading ? <Spin size="small" /> : <HeartFilled className="text-xs" style={{ color: "#d1d5db" }} />}
        </div>
        {/* 分享 */}
        <div
          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-white shadow-sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShareModalOpen(true);
          }}
          data-qt="mall_product_share_click"
          data-qt-entry="grid_mobile"
        >
          <ShareAltOutlined className="text-xs" style={{ color: "#d1d5db" }} />
        </div>
      </div>
    );
  };

  const ProductImage = () => (
    <div className="relative flex h-[43.73vw] w-full items-center justify-center overflow-hidden rounded-tl-xl rounded-tr-xl bg-gray-200 md:h-[30.12vw] lg:h-[240px] xl:h-[308px]">
      <div className="relative h-[36vw] w-[36vw] md:h-[22vw] md:w-[22vw] lg:h-[180px] lg:w-[180px] xl:h-[222px] xl:w-[222px]">
        <Image
          src={safeProduct.image[currentLocale]}
          alt={safeProduct.storeName[currentLocale]}
          fill
          style={{ objectFit: "cover" }}
          priority={false}
          sizes="(max-width: 768px) 36vw, (max-width: 1024px) 22vw, 180px"
          quality={80}
        />
      </div>
      <DetailLink id={productId!} isMobile={true} />
      <FavoriteIcon />
      <MobileActionIcons />
      {!isOffShelf && (
        <div className="absolute left-0 top-0 hidden h-full w-full bg-[rgba(0,0,0,0.5)] group-hover:hidden lg:group-hover:block">
          <DetailLink id={productId!} isMobile={false} />
          <div className="absolute bottom-0 left-0 z-20 flex h-11 w-full items-center justify-between bg-primary text-white">
            {safeProduct.productMode !== 3 && (
              <>
                <button
                  className={`relative flex h-full flex-1 items-center justify-center ${
                    safeProduct.stock === 0 ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={debouncedHandleAddToCart}
                  aria-label={
                    safeProduct.stock === 0 ? t("product_detail.sell_out") : t("product_detail.add_to_the_cart")
                  }
                  disabled={safeProduct.stock === 0 || cartLoading}
                  type="button"
                  data-qt="mall_product_add_to_cart"
                  data-qt-entry="grid_hover"
                >
                  <Icon name="cart-white" size={20} title="icon" className="mr-2" />
                  {safeProduct.stock === 0 ? t("product_detail.sell_out") : t("product_detail.add_to_the_cart")}
                  <Spin spinning={cartLoading} className="absolute left-0 top-0 h-full w-full" />
                </button>

                <div className="h-7 w-[1px] bg-gray-600 "></div>
              </>
            )}
            <button
              className="flex h-full flex-1 items-center justify-center"
              onClick={() => setShareModalOpen(true)}
              aria-label={t("common.share")}
              type="button"
              data-qt="mall_product_share_click"
              data-qt-entry="grid_hover"
            >
              <Icon name="share-white" size={20} title="icon" className="mr-2" />
              {t("product_detail.share")}
            </button>
          </div>
        </div>
      )}
      {safeProduct.stock === 0 && !isOffShelf && <SoldOutTip />}
      {isOffShelf && <OffShelfTip />}
    </div>
  );

  const ProductDesc = () => (
    <Link
      href={isOffShelf ? "#" : `/mall/${productId}`}
      locale={router.locale}
      onClick={(e) => isOffShelf && e.preventDefault()}
      data-qt="mall_product_detail_click"
      data-qt-entry="grid_desc"
    >
      <div
        className={`flex flex-col rounded-bl-xl rounded-br-xl border border-gray-900 px-mobile py-2 md:px-4 md:py-6 ${
          isOffShelf ? "cursor-not-allowed" : ""
        }`}
      >
        <div className="mb-1 flex items-center justify-between text-sm md:mb-[6px] md:text-base">
          <div
            className="flex-1 truncate font-semibold text-stone-800 transition-colors group-hover:text-primary"
            title={safeProduct.storeName[currentLocale]}
          >
            {safeProduct.storeName[currentLocale]}
          </div>
          <div className="ml-4 hidden shrink-0 font-semibold text-primary md:block">{safeProduct.price}</div>
        </div>
        <div className="flex items-center justify-start md:justify-between">
          <div className="block shrink-0 text-sm font-semibold text-primary md:hidden">{safeProduct.price}</div>
          <div className="hidden flex-1 truncate text-xs text-stone-300 md:flex">
            {safeProduct.categories.map((category, index) => (
              <div className={index !== safeProduct.categories.length - 1 ? "mr-1" : ""} key={index}>
                {category.name[currentLocale]}
              </div>
            ))}
          </div>
          <div className="ml-1 shrink-0 text-[8px] text-stone-100 sm:text-xs md:ml-4">{t("common.dream_value")}</div>
        </div>
      </div>
    </Link>
  );

  // 横向列表布局
  if (layout === "list") {
    return (
      <div
        className="group relative flex cursor-pointer overflow-hidden rounded-xl border border-gray-900 bg-white transition-shadow hover:shadow-md"
        // 同 grid 布局，商品参数挂容器统一继承
        data-qt-goods-id={productId}
        data-qt-goods-name={safeProduct.storeName?.[currentLocale]}
        data-qt-price={safeProduct.price}
        data-qt-stock={safeProduct.stock}
        data-qt-layout="list"
      >
        {/* 左侧图片 */}
        <Link
          href={`/mall/${safeProduct.id}`}
          locale={router.locale}
          className="relative flex h-[90px] w-[90px] shrink-0 items-center justify-center bg-gray-200 lg:h-[120px] lg:w-[120px] xl:h-[130px] xl:w-[130px]"
          data-qt="mall_product_detail_click"
          data-qt-entry="list_image"
        >
          <div className="relative h-[66px] w-[66px] lg:h-[90px] lg:w-[90px] xl:h-[100px] xl:w-[100px]">
            <Image
              src={safeProduct.image[currentLocale]}
              alt={safeProduct.storeName[currentLocale]}
              fill
              style={{ objectFit: "cover" }}
              priority={false}
              sizes="120px"
              quality={80}
            />
          </div>
          {safeProduct.stock === 0 && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[rgba(256,256,256,0.5)] text-sm text-white"></div>
          )}
        </Link>
        {/* 中间信息 */}
        <Link
          href={`/mall/${safeProduct.id}`}
          locale={router.locale}
          className="flex flex-1 flex-col justify-center px-3 py-2 lg:px-5 lg:py-3"
          data-qt="mall_product_detail_click"
          data-qt-entry="list_info"
        >
          <div className="mb-1 flex items-center font-semibold text-stone-800 transition-colors group-hover:text-primary lg:mb-1.5 text-sm xl:text-base">
            {safeProduct.storeName[currentLocale]}
            {safeProduct.stock === 0 && (
              <span className="line ml-1 inline-flex h-4 items-center rounded-sm bg-[#bbb] px-1 text-[10px] text-white">
                {t("product_detail.sell_out")}
              </span>
            )}
          </div>
          {safeProduct.categories?.length > 0 && (
            <div className="mb-1 hidden flex-wrap gap-1.5 lg:mb-2 lg:flex">
              {safeProduct.categories.map((category, index) => (
                <span key={index} className="rounded border bg-gray-200 px-2 py-0.5 text-xs text-stone-300">
                  {category.name[currentLocale]}
                </span>
              ))}
            </div>
          )}
          {safeProduct.storeInfo && (
            <div className="line-clamp-1 hidden text-xs text-stone-300 lg:block">
              {safeProduct.storeInfo[currentLocale]}
            </div>
          )}
          {/* mobile 价格（显示在中间列） */}
          <div className="mt-1 text-base font-bold text-primary lg:hidden">
            {safeProduct.price}
            <span className="text-[10px] text-stone-300 ml-1">{t("common.dream_value")}</span>
          </div>
        </Link>

        {/* 右侧价格 + 操作 */}
        <div className="flex shrink-0 flex-col items-end justify-center p-2 lg:justify-between lg:p-5">
          {/* 价格（PC） */}
          <div className="hidden text-right lg:block">
            <div className="text-lg font-bold text-primary">{safeProduct.price}</div>
            <div className="text-xs text-stone-300">{t("common.dream_value")}</div>
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center gap-1.5 lg:gap-3">
            {/* 收藏 */}
            <span
              className="flex cursor-pointer items-center gap-1 text-base text-gray-400 transition-colors hover:text-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                debouncedAddFavorite();
              }}
              data-qt="mall_product_favorite_click"
            >
              {favoriteLoading ? <Spin size="small" /> : <HeartOutlined />}
              <span className="hidden lg:inline">{t("favorites.favorite")}</span>
            </span>

            {/* 分享 */}
            <span
              className="flex cursor-pointer items-center gap-1 text-base text-gray-400 transition-colors hover:text-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShareModalOpen(true);
              }}
              data-qt="mall_product_share_click"
            >
              <ShareAltOutlined />
              <span className="hidden lg:inline">{t("product_detail.share")}</span>
            </span>

            {/* 加入购物车 */}
            {
              <button
                className={`flex items-center gap-1 rounded-md border border-primary px-2 py-1 text-sm text-primary transition-colors hover:bg-[#fde4e1] hover:text-primary lg:px-3 lg:py-1.5 ${
                  safeProduct.stock === 0 ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  debouncedHandleAddToCart();
                }}
                disabled={safeProduct.stock === 0 || cartLoading}
                type="button"
                data-qt="mall_product_add_to_cart"
              >
                <Icon name="cart-red" size={14} title="icon" />
                <span>
                  {safeProduct.stock === 0 ? t("product_detail.sell_out") : t("product_detail.add_to_the_cart")}
                </span>
                {cartLoading && <Spin size="small" className="ml-1" />}
              </button>
            }
          </div>
        </div>

        <ProductShareModal open={shareModalOpen} product={safeProduct} onModalCancel={() => setShareModalOpen(false)} />
        <AddCartSuccessModal
          onRef={AddCartSuccessRef}
          imgSrc="/images/cart/cart.webp"
          autoClose={true}
          desc={t("product_detail.add_to_the_cart") + t("common.success")}
        />
      </div>
    );
  }

  // 默认 grid 布局
  return (
    <div
      // group 挂在卡片根上：图片区与文字区是兄弟节点，挂在图片上时 hover 文字带不出蒙层
      className={`group flex flex-col overflow-hidden ${isOffShelf ? "cursor-not-allowed" : "cursor-pointer"}`}
      // 商品业务参数挂在卡片容器上，卡片内所有按钮点击自动继承，无需逐个重复
      data-qt-goods-id={productId}
      data-qt-goods-name={safeProduct.storeName?.[currentLocale]}
      data-qt-price={safeProduct.price}
      data-qt-stock={safeProduct.stock}
      data-qt-layout="grid"
    >
      <ProductImage />
      <ProductDesc />

      <ProductShareModal open={shareModalOpen} product={safeProduct} onModalCancel={() => setShareModalOpen(false)} />

      <AddCartSuccessModal
        onRef={AddCartSuccessRef}
        imgSrc="/images/cart/cart.webp"
        autoClose={true}
        desc={t("product_detail.add_to_the_cart") + t("common.success")}
      />
    </div>
  );
};

export default ProductItem;
